/**
 * Query key factory.
 *
 * Cache keys are centralised so invalidation is reliable. The pattern is hierarchical: invalidating
 * `assets.all` clears every asset list and detail at once, which is what a mutation usually needs,
 * while a targeted key avoids refetching the world after a small change.
 *
 * Keys include their filter object, so two different filter combinations are two cache entries and
 * changing a filter never shows the previous result.
 */
export const queryKeys = {
  me: ['me'] as const,
  profile: ['me', 'profile'] as const,

  organization: {
    all: ['organization'] as const,
    current: () => [...queryKeys.organization.all, 'current'] as const,
    departments: () => [...queryKeys.organization.all, 'departments'] as const,
    locations: () => [...queryKeys.organization.all, 'locations'] as const,
  },

  catalog: {
    all: ['catalog'] as const,
    assetTypes: () => [...queryKeys.catalog.all, 'asset-types'] as const,
    assetModels: (assetTypeId?: string) =>
      [...queryKeys.catalog.all, 'asset-models', assetTypeId ?? 'all'] as const,
    manufacturers: () => [...queryKeys.catalog.all, 'manufacturers'] as const,
    vendors: () => [...queryKeys.catalog.all, 'vendors'] as const,
    lifecycleRules: () => [...queryKeys.catalog.all, 'lifecycle-rules'] as const,
  },

  attachments: {
    owner: (type: string, id: string) => ['attachments', type, id] as const,
    list: (type: string, id: string, params: unknown) => [...queryKeys.attachments.owner(type, id), params] as const,
  },

  assets: {
    all: ['assets'] as const,
    list: (params: unknown) => [...queryKeys.assets.all, 'list', params] as const,
    detail: (assetId: string) => [...queryKeys.assets.all, 'detail', assetId] as const,
    lifecycle: (assetId: string, params: unknown) =>
      [...queryKeys.assets.all, 'lifecycle', assetId, params] as const,
    healthChecks: (assetId: string) => [...queryKeys.assets.all, 'health-checks', assetId] as const,
  },

  employees: {
    all: ['employees'] as const,
    list: (params: unknown) => [...queryKeys.employees.all, 'list', params] as const,
    detail: (employeeId: string) => [...queryKeys.employees.all, 'detail', employeeId] as const,
  },

  assignments: {
    all: ['assignments'] as const,
    list: (params: unknown) => [...queryKeys.assignments.all, 'list', params] as const,
    mine: (params: unknown) => [...queryKeys.assignments.all, 'mine', params] as const,
    detail: (assignmentId: string) =>
      [...queryKeys.assignments.all, 'detail', assignmentId] as const,
  },

  acceptance: {
    all: ['acceptance'] as const,
    detail: (acceptanceId: string) => [...queryKeys.acceptance.all, 'detail', acceptanceId] as const,
    rejectionReasons: () => [...queryKeys.acceptance.all, 'rejection-reasons'] as const,
  },

  procurement: {
    all: ['procurement'] as const,
    list: (params: unknown) => [...queryKeys.procurement.all, 'list', params] as const,
    detail: (orderId: string) => [...queryKeys.procurement.all, 'detail', orderId] as const,
  },

  preparation: {
    all: ['preparation'] as const,
    list: (params: unknown) => [...queryKeys.preparation.all, 'list', params] as const,
    detail: (runId: string) => [...queryKeys.preparation.all, 'detail', runId] as const,
    byAsset: (assetId: string) => [...queryKeys.preparation.all, 'by-asset', assetId] as const,
  },

  notifications: {
    all: ['notifications'] as const,
    mine: (params: unknown) => [...queryKeys.notifications.all, 'mine', params] as const,
    unreadCount: () => [...queryKeys.notifications.all, 'unread-count'] as const,
  },

  audit: {
    all: ['audit'] as const,
    list: (params: unknown) => [...queryKeys.audit.all, 'list', params] as const,
  },

  outbox: {
    all: ['outbox'] as const,
    list: (params: unknown) => [...queryKeys.outbox.all, 'list', params] as const,
    stats: () => [...queryKeys.outbox.all, 'stats'] as const,
  },

  reports: {
    all: ['reports'] as const,
    assetSummary: (category?: string) =>
      [...queryKeys.reports.all, 'asset-summary', category ?? 'all'] as const,
    bookValue: (category?: string) => [...queryKeys.reports.all, 'book-value', category ?? 'all'] as const,
    assetScores: (category?: string) =>
      [...queryKeys.reports.all, 'asset-scores', category ?? 'all'] as const,
    warrantyExpiry: (months: number) =>
      [...queryKeys.reports.all, 'warranty-expiry', months] as const,
    acceptanceTrend: (months: number) =>
      [...queryKeys.reports.all, 'acceptance-trend', months] as const,
    spend: (months: number) => [...queryKeys.reports.all, 'spend', months] as const,
  },

  tickets: {
    all: ['tickets'] as const,
    list: (params: unknown) => [...queryKeys.tickets.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.tickets.all, 'detail', id] as const,
    comments: (id: string) => [...queryKeys.tickets.all, 'comments', id] as const,
    summary: () => [...queryKeys.tickets.all, 'summary'] as const,
    assignees: () => [...queryKeys.tickets.all, 'assignees'] as const,
  },

  imports: {
    all: ['imports'] as const,
    history: (params: unknown) => [...queryKeys.imports.all, 'history', params] as const,
    errors: (jobId: string) => [...queryKeys.imports.all, 'errors', jobId] as const,
  },

  discovery: {
    all: ['discovery'] as const,
    summary: () => [...queryKeys.discovery.all, 'summary'] as const,
    ranges: () => [...queryKeys.discovery.all, 'ranges'] as const,
    devices: (params: unknown) => [...queryKeys.discovery.all, 'devices', params] as const,
    runs: (params: unknown) => [...queryKeys.discovery.all, 'runs', params] as const,
    collectors: () => [...queryKeys.discovery.all, 'collectors'] as const,
    collectorDownloads: () => [...queryKeys.discovery.all, 'collector-downloads'] as const,
  },

  admin: {
    all: ['admin'] as const,
    departments: () => [...queryKeys.admin.all, 'departments'] as const,
    locations: () => [...queryKeys.admin.all, 'locations'] as const,
    assetTypes: () => [...queryKeys.admin.all, 'asset-types'] as const,
    assetModels: () => [...queryKeys.admin.all, 'asset-models'] as const,
    manufacturers: () => [...queryKeys.admin.all, 'manufacturers'] as const,
    vendors: () => [...queryKeys.admin.all, 'vendors'] as const,
    companyRoles: () => [...queryKeys.admin.all, 'company-roles'] as const,
    assignablePermissions: () => [...queryKeys.admin.all, 'assignable-permissions'] as const,
    users: (params: unknown) => [...queryKeys.admin.all, 'users', params] as const,
    roles: () => [...queryKeys.admin.all, 'roles'] as const,
    permissions: () => [...queryKeys.admin.all, 'permissions'] as const,
    directory: () => [...queryKeys.admin.all, 'directory'] as const,
    directoryAccount: (userId: string) =>
      [...queryKeys.admin.all, 'directory-account', userId] as const,
    sessionPolicy: () => [...queryKeys.admin.all, 'session-policy'] as const,
  },

  platform: {
    all: ['platform'] as const,
    summary: () => [...queryKeys.platform.all, 'summary'] as const,
    tenants: (params: unknown) => [...queryKeys.platform.all, 'tenants', params] as const,
    tenant: (organizationId: string) => [...queryKeys.platform.all, 'tenant', organizationId] as const,
    modules: (organizationId: string) =>
      [...queryKeys.platform.all, 'modules', organizationId] as const,
    invoices: (organizationId: string, params: unknown) =>
      [...queryKeys.platform.all, 'invoices', organizationId, params] as const,
    plans: () => [...queryKeys.platform.all, 'plans'] as const,
    softwareLicences: (organizationId: string, params: unknown) =>
      [...queryKeys.platform.all, 'software-licences', organizationId, params] as const,
    softwareLicence: (organizationId: string, id: string) =>
      [...queryKeys.platform.all, 'software-licence', organizationId, id] as const,
  },

  subscription: {
    all: ['subscription'] as const,
    mine: () => [...queryKeys.subscription.all, 'mine'] as const,
    invoices: (params: unknown) => [...queryKeys.subscription.all, 'invoices', params] as const,
  },

  consumables: {
    all: ['consumables'] as const,
    list: (params: unknown) => [...queryKeys.consumables.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.consumables.all, 'detail', id] as const,
    movements: (id: string, params: unknown) =>
      [...queryKeys.consumables.all, 'movements', id, params] as const,
    categories: () => [...queryKeys.consumables.all, 'categories'] as const,
  },

  maintenance: {
    all: ['maintenance'] as const,
    summary: () => [...queryKeys.maintenance.all, 'summary'] as const,
    workOrders: (params: unknown) => [...queryKeys.maintenance.all, 'work-orders', params] as const,
    workOrder: (id: string) => [...queryKeys.maintenance.all, 'work-order', id] as const,
    schedules: (params: unknown) => [...queryKeys.maintenance.all, 'schedules', params] as const,
  },

  contracts: {
    all: ['contracts'] as const,
    summary: () => [...queryKeys.contracts.all, 'summary'] as const,
    list: (params: unknown) => [...queryKeys.contracts.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.contracts.all, 'detail', id] as const,
    successor: (id: string) => [...queryKeys.contracts.all, 'successor', id] as const,
    covering: (assetId: string) => [...queryKeys.contracts.all, 'covering', assetId] as const,
  },

  endpoints: {
    all: ['endpoints'] as const,
    monitoringSettings: (endpointId: string) => [...queryKeys.endpoints.all, 'monitoring-settings', endpointId] as const,
    summary: () => [...queryKeys.endpoints.all, 'summary'] as const,
    byAsset: (assetId: string) => [...queryKeys.endpoints.all, 'by-asset', assetId] as const,
    detail: (endpointId: string) => [...queryKeys.endpoints.all, 'detail', endpointId] as const,
    facts: (endpointId: string) => [...queryKeys.endpoints.all, 'facts', endpointId] as const,
    alerts: (endpointId: string) => [...queryKeys.endpoints.all, 'alerts', endpointId] as const,
    uninstallAuthorizations: (endpointId: string) =>
      [...queryKeys.endpoints.all, 'uninstall-authorizations', endpointId] as const,
    software: (endpointId: string, params: unknown) =>
      [...queryKeys.endpoints.all, 'software', endpointId, params] as const,
    softwareChanges: (endpointId: string, params: unknown) =>
      [...queryKeys.endpoints.all, 'software-changes', endpointId, params] as const,
    list: (params: unknown) => [...queryKeys.endpoints.all, 'list', params] as const,
    enrollSecrets: () => [...queryKeys.endpoints.all, 'enroll-secrets'] as const,
    installerStatus: () => [...queryKeys.endpoints.all, 'installer-status'] as const,
    groups: () => [...queryKeys.endpoints.all, 'groups'] as const,
    group: (groupId: string) => [...queryKeys.endpoints.all, 'groups', groupId] as const,
  },

  locations: {
    all: ['locations'] as const,
    device: (endpointId: string) => [...queryKeys.locations.all, 'device', endpointId] as const,
    fleet: () => [...queryKeys.locations.all, 'fleet'] as const,
    settings: () => [...queryKeys.locations.all, 'settings'] as const,
  },

  softwareLicences: {
    all: ['software-licences'] as const,
    list: (params: unknown) => [...queryKeys.softwareLicences.all, 'list', params] as const,
    detail: (id: string) => [...queryKeys.softwareLicences.all, 'detail', id] as const,
  },

  software: {
    all: ['software'] as const,
    titles: (params: unknown) => [...queryKeys.software.all, 'titles', params] as const,
    installs: (params: unknown) => [...queryKeys.software.all, 'installs', params] as const,
    versions: (name: string) => [...queryKeys.software.all, 'versions', name] as const,
    changes: (params: unknown) => [...queryKeys.software.all, 'changes', params] as const,
  },

  policies: {
    all: ['policies'] as const,
    settings: () => [...queryKeys.policies.all, 'settings'] as const,
    list: (status: unknown) => [...queryKeys.policies.all, 'list', status] as const,
    detail: (policyId: string) => [...queryKeys.policies.all, 'detail', policyId] as const,
    validation: (policyId: string) => [...queryKeys.policies.all, 'validation', policyId] as const,
    versions: (policyId: string) => [...queryKeys.policies.all, 'versions', policyId] as const,
    version: (policyId: string, number: number) =>
      [...queryKeys.policies.all, 'versions', policyId, number] as const,
    deployments: (params: unknown) => [...queryKeys.policies.all, 'deployments', params] as const,
    events: (params: unknown) => [...queryKeys.policies.all, 'events', params] as const,
    byDevice: (endpointId: string) => [...queryKeys.policies.all, 'by-device', endpointId] as const,
  },
} as const;
